import { supabase } from '../lib/supabaseClient';
import { ProductionRecord, ServiceCutoff } from '../types';

export interface PredictedConsumption {
  electricityKwh: number;
  waterM3: number;
  steamTn: number;
  co2Tn: number;
}

// Coeficientes de predicción por línea de producción
export const LINE_COEFFICIENTS: Record<'L1' | 'L3', {
  electricity: { base: number; perHl: number; cip: number; formatChange: number; maint: number };
  water: { base: number; perHl: number; cip: number; formatChange: number; maint: number };
  steam: { base: number; perHl: number; cip: number; formatChange: number; maint: number };
  co2: { base: number; perHl: number; cip: number; formatChange: number; maint: number };
}> = {
  L1: {
    electricity: { base: 100, perHl: 0.8, cip: 80, formatChange: 30, maint: 20 },
    water: { base: 1.2, perHl: 0.12, cip: 2.0, formatChange: 0.3, maint: 0.1 },
    steam: { base: 0.1, perHl: 0.015, cip: 0.5, formatChange: 0.1, maint: 0.05 },
    co2: { base: 0.01, perHl: 0.002, cip: 0, formatChange: 0.005, maint: 0 }
  },
  L3: {
    electricity: { base: 180, perHl: 1.1, cip: 150, formatChange: 50, maint: 30 },
    water: { base: 2.0, perHl: 0.18, cip: 3.5, formatChange: 0.5, maint: 0.2 },
    steam: { base: 0.2, perHl: 0.022, cip: 0.9, formatChange: 0.15, maint: 0.08 },
    co2: { base: 0.015, perHl: 0.003, cip: 0, formatChange: 0.008, maint: 0 }
  }
};

export const predictionService = {
  /**
   * Obtiene todos los registros de producción programados o reales entre dos fechas.
   */
  async fetchProductionRecords(startDate: string, endDate: string): Promise<ProductionRecord[]> {
    const { data, error } = await supabase
      .from('production_records')
      .select('*')
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: true });

    if (error) throw error;

    return (data || []).map(row => ({
      id: row.id,
      date: row.date,
      lineId: row.line_id as 'L1' | 'L3',
      projectedVolumeHl: Number(row.projected_volume_hl || 0),
      plannedEvents: row.planned_events || [],
      realVolumeHl: Number(row.real_volume_hl || 0),
      realEvents: row.real_events || []
    }));
  },

  /**
   * Guarda o actualiza un registro de producción programada o real.
   */
  async saveProductionRecord(record: Omit<ProductionRecord, 'id'>): Promise<void> {
    const { error } = await supabase
      .from('production_records')
      .upsert({
        date: record.date,
        line_id: record.lineId,
        projected_volume_hl: record.projectedVolumeHl,
        planned_events: record.plannedEvents,
        real_volume_hl: record.realVolumeHl,
        real_events: record.realEvents
      }, {
        onConflict: 'date,line_id'
      });

    if (error) throw error;
  },

  /**
   * Obtiene todos los cortes de servicio planificados o reales entre dos fechas.
   */
  async fetchServiceCutoffs(startDate: string, endDate: string): Promise<ServiceCutoff[]> {
    const { data, error } = await supabase
      .from('service_cutoffs')
      .select('*')
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: true });

    if (error) throw error;

    return (data || []).map(row => ({
      id: row.id,
      date: row.date,
      lineId: row.line_id as 'L1' | 'L3' | 'Elaboración',
      serviceType: row.service_type as 'electricity' | 'water' | 'steam' | 'co2',
      plannedStartTime: row.planned_start_time,
      plannedEndTime: row.planned_end_time,
      cutoffReason: row.cutoff_reason || undefined,
      isConfirmed: !!row.is_confirmed,
      actualStartTime: row.actual_start_time || undefined,
      actualEndTime: row.actual_end_time || undefined,
      operatorComment: row.operator_comment || undefined,
      confirmedBy: row.confirmed_by || undefined,
      confirmedAt: row.confirmed_at || undefined
    }));
  },

  /**
   * Guarda o actualiza un registro de corte de servicio.
   */
  async saveServiceCutoff(cutoff: Omit<ServiceCutoff, 'id'> & { id?: string }): Promise<void> {
    const payload = {
      date: cutoff.date,
      line_id: cutoff.lineId,
      service_type: cutoff.serviceType,
      planned_start_time: cutoff.plannedStartTime,
      planned_end_time: cutoff.plannedEndTime,
      cutoff_reason: cutoff.cutoffReason || null,
      is_confirmed: cutoff.isConfirmed,
      actual_start_time: cutoff.actualStartTime || null,
      actual_end_time: cutoff.actualEndTime || null,
      operator_comment: cutoff.operatorComment || null,
      confirmed_by: cutoff.confirmedBy || null,
      confirmed_at: cutoff.confirmedAt || null
    };

    let error;
    if (cutoff.id) {
      const { error: err } = await supabase
        .from('service_cutoffs')
        .update(payload)
        .eq('id', cutoff.id);
      error = err;
    } else {
      const { error: err } = await supabase
        .from('service_cutoffs')
        .insert(payload);
      error = err;
    }

    if (error) throw error;
  },

  /**
   * Elimina un corte de servicio por su ID.
   */
  async deleteServiceCutoff(cutoffId: string): Promise<void> {
    const { error } = await supabase
      .from('service_cutoffs')
      .delete()
      .eq('id', cutoffId);

    if (error) throw error;
  },

  /**
   * Calcula la predicción teórica de consumo basado en hectolitros, eventos y horas de cortes de servicio.
   */
  calculatePrediction(
    lineId: 'L1' | 'L3',
    volumeHl: number,
    events: string[],
    cutoffHours: Record<'electricity' | 'water' | 'steam' | 'co2', number> = { electricity: 0, water: 0, steam: 0, co2: 0 }
  ): PredictedConsumption {
    const coefs = LINE_COEFFICIENTS[lineId];
    
    const hasCip = events.includes('CIP');
    const hasMaint = events.includes('Mantenimiento');
    const hasFormat = events.includes('Cambio Formato');

    const compute = (param: 'electricity' | 'water' | 'steam' | 'co2') => {
      const c = coefs[param];
      const isActive = volumeHl > 0 || events.length > 0;
      
      // Restamos las horas de corte de servicio de la carga base
      const hoursWithService = Math.max(0, 24 - (cutoffHours[param] || 0));
      const baseLoad = isActive ? (c.base / 24) * hoursWithService : 0;
      
      const variableLoad = volumeHl * c.perHl;
      const cipExtra = hasCip ? c.cip : 0;
      const formatExtra = hasFormat ? c.formatChange : 0;
      const maintExtra = hasMaint ? c.maint : 0;

      return baseLoad + variableLoad + cipExtra + formatExtra + maintExtra;
    };

    return {
      electricityKwh: compute('electricity'),
      waterM3: compute('water'),
      steamTn: compute('steam'),
      co2Tn: compute('co2')
    };
  }
};
